import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, ExternalLink, Globe, Square, X } from 'lucide-react';
import { ConfirmModal } from '../../../ui/Modal';
import { useAuthStore } from '../../../../store/authStore';
import { setUpgradeModalOpen, setUpgradeReason } from '../../../../store/uiModalStore';
import QrCode from './QrCode';
import { copyText, fetchPlans, openLink, tunnelUsage } from './shareApi';
import { formatBytes, formatCountdown, shareAccess, tunnelsLimit } from './shareGate';
import { dismissEnded, isSharing, startShare, stopShare, useShareStore } from './shareStore';
import './Share.css';

let plansOnce = null;
/** The plans catalog, fetched at most once per session (null on failure). */
function loadPlansOnce() {
  if (!plansOnce) plansOnce = fetchPlans().catch(() => null);
  return plansOnce;
}
const gateByUser = new Map(); // user id -> Promise<usage | null>
/** GET /tunnels/usage once per account; null when the call failed. */
function loadGateOnce(userId) {
  if (!gateByUser.has(userId)) gateByUser.set(userId, tunnelUsage().catch(() => null));
  return gateByUser.get(userId);
}
export function resetSharePlansForTests() {
  plansOnce = null;
  gateByUser.clear();
}

/**
 * 'allowed' | 'upgrade' | 'unavailable' | 'suspended' | 'hidden' for the
 * signed-in user (shareGate.js). `GET /tunnels/usage` decides; `/auth/usage`
 * and `/plans` only when that call fails.
 */
export function useShareAccess() {
  const user = useAuthStore((s) => s.user);
  const disabled = useShareStore((s) => s.disabled);
  const polled = useShareStore((s) => s.usage); // the meter's fresher copy of the same endpoint
  const userId = user?.id ?? null;
  const [gate, setGate] = useState({ userId: null, usage: undefined });
  const [plans, setPlans] = useState(null);
  useEffect(() => {
    if (userId == null) return undefined;
    let alive = true;
    loadGateOnce(userId).then((usage) => {
      if (alive) setGate({ userId, usage });
    });
    return () => {
      alive = false;
    };
  }, [userId]);
  const usage = polled && (polled.enabled !== null || polled.tunnelsLimit !== null) ? polled : gate.userId === userId ? gate.usage : undefined;
  // Failed, or an answer that names neither the switch nor the limit: use the fallbacks.
  const gateFailed = usage === null || (!!usage && usage.enabled === null && usage.tunnelsLimit === null);
  const needPlans = gateFailed && !!user && typeof user?._usage?.limits?.tunnels_limit !== 'number';
  useEffect(() => {
    if (!needPlans) return undefined;
    let alive = true;
    loadPlansOnce().then((p) => {
      if (alive) setPlans(p);
    });
    return () => {
      alive = false;
    };
  }, [needPlans]);
  if (!user) return 'hidden';
  if (usage === undefined && !disabled) return 'hidden'; // not answered yet
  return shareAccess({ limit: gateFailed ? tunnelsLimit(user, plans) : null, disabled, usage: gateFailed ? null : usage });
}

function openUpgrade(t) {
  setUpgradeReason(t('projects.run.share.upgradeReason'));
  setUpgradeModalOpen(true);
}

/**
 * "Share" on a running row. Pro/Team: confirm, then the link card below the
 * row takes over. Free/Basic: the plan upgrade dialog. Switch off: disabled.
 */
export function ShareButton({ runKey, run, config }) {
  const { t } = useTranslation();
  const access = useShareAccess();
  const share = useShareStore((s) => s.shares[runKey]);
  const [confirming, setConfirming] = useState(false);
  if (access === 'hidden' || !config?.port || run?.status !== 'ready' || !run?.sid || isSharing(share)) return null;

  const onClick = () => {
    if (access === 'upgrade') openUpgrade(t);
    else if (access === 'allowed') setConfirming(true);
  };
  const confirm = async () => {
    setConfirming(false);
    const res = await startShare(runKey, { runId: run.sid, port: config.port, label: config.name });
    if (res.ok) return;
    if (res.kind === 'plan_limit') openUpgrade(t);
    else if (res.kind === 'error') {
      useShareStore.setState((s) => ({ ended: { ...s.ended, [runKey]: 'create_failed' } }));
    }
  };

  return (
    <>
      <button
        type="button"
        className="gd-btn pj-share-btn"
        data-testid="run-share"
        data-access={access}
        onClick={onClick}
        disabled={access === 'unavailable' || access === 'suspended'}
        title={access === 'unavailable' ? t('projects.run.share.unavailable') : access === 'suspended' ? t('projects.run.share.ended.share_suspended') : t('projects.run.share.tooltip')}
      >
        <Globe size={13} /> {t('projects.run.share.share')}
        {access === 'upgrade' && <span className="pj-share-pro">Pro</span>}
      </button>
      <ConfirmModal
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={confirm}
        type="warning"
        title={t('projects.run.share.confirmTitle')}
        message={
          <span data-testid="share-confirm">
            {t('projects.run.share.confirmMessage', { port: config.port })}
            <span className="pj-share-confirm-hint">{t('projects.run.share.confirmHint')}</span>
          </span>
        }
        confirmText={t('projects.run.share.confirm')}
        cancelText={t('common.cancel')}
      />
    </>
  );
}

function useNow(active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

function BandwidthMeter({ share }) {
  const { t } = useTranslation();
  const usage = useShareStore((s) => s.usage);
  const mine = (share.bytesIn || 0) + (share.bytesOut || 0);
  const cap = usage?.cap;
  const used = usage?.used ?? 0;
  const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  return (
    <div className="pj-share-meter" data-testid="share-bandwidth">
      <span className="pj-share-label">{t('projects.run.share.bandwidth')}</span>
      {cap ? (
        <>
          <span className="pj-share-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
            <span className={`pj-share-bar-fill${pct >= 90 ? ' pj-share-bar-fill--high' : ''}`} style={{ width: `${pct}%` }} />
          </span>
          <span className="pj-share-meta">{t('projects.run.share.bandwidthUsed', { used: formatBytes(used), cap: formatBytes(cap) })}</span>
        </>
      ) : (
        <span className="pj-share-meta">{t('projects.run.share.bandwidthNoCap', { used: formatBytes(usage ? used : mine) })}</span>
      )}
      <span className="pj-share-meta">{t('projects.run.share.thisLink', { bytes: formatBytes(mine) })}</span>
    </div>
  );
}

function statusClass(code) {
  if (code >= 500 || !code) return 'pj-share-code--bad';
  if (code >= 400) return 'pj-share-code--warn';
  return 'pj-share-code--ok';
}

function RequestLog({ log }) {
  const { t } = useTranslation();
  return (
    <div className="pj-share-log" data-testid="share-log">
      <div className="pj-share-label">{t('projects.run.share.log')}</div>
      {log.length === 0 ? (
        <p className="pj-share-meta">{t('projects.run.share.logEmpty')}</p>
      ) : (
        <ul className="pj-share-log-list">
          {log.map((l, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${l.at}-${i}`} className="pj-share-log-row" data-testid="share-log-row">
              <span className="pj-share-log-method">{l.method}</span>
              <span className="pj-share-log-path" title={l.path}>
                {l.path}
              </span>
              <span className={`pj-share-code ${statusClass(l.status)}`}>{l.status || '—'}</span>
              <span className="pj-share-log-ms">{l.ms} ms</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ENDED_KEYS = ['expired', 'signed_out', 'open_failed', 'create_failed', 'unreachable', 'closed_by_user', 'killed', 'heartbeat_timeout', 'replaced', 'tunnel_closed', 'share_suspended'];

/** The link card (or why sharing ended) under a run row. */
export function ShareCard({ runKey, run }) {
  const { t } = useTranslation();
  const share = useShareStore((s) => s.shares[runKey]);
  const ended = useShareStore((s) => s.ended[runKey]);
  const access = useShareAccess();
  const [copied, setCopied] = useState(false);
  const now = useNow(!!share?.expiresAt);

  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(id);
  }, [copied]);

  if (!share) {
    if (ended) {
      const key = ENDED_KEYS.includes(ended) ? ended : 'other';
      return (
        <div className="pj-share-ended" role="status" data-testid="share-ended" data-reason={ended}>
          {t(`projects.run.share.ended.${key}`)}
          <button type="button" className="graph-icon-btn" aria-label={t('projects.run.share.dismiss')} onClick={() => dismissEnded(runKey)}>
            <X size={12} />
          </button>
        </div>
      );
    }
    if (access === 'suspended' && run?.status === 'ready') {
      return (
        <div className="pj-share-ended" role="status" data-testid="share-unavailable">
          {t('projects.run.share.ended.share_suspended')}
        </div>
      );
    }
    if (access === 'unavailable' && run?.status === 'ready') {
      return (
        <div className="pj-share-ended" role="status" data-testid="share-unavailable">
          {t('projects.run.share.unavailable')}
        </div>
      );
    }
    return null;
  }

  const url = share.publicUrl;
  const copy = () => {
    copyText(url)
      .then(() => setCopied(true))
      .catch(() => {});
  };
  return (
    <div className="pj-share-card" data-testid="share-card" data-state={share.state}>
      <div className="pj-share-main">
        {url ? <QrCode text={url} size={112} label={t('projects.run.share.qrLabel', { url })} /> : <div className="pj-share-qr-placeholder" />}
        <div className="pj-share-info">
          <div className="pj-share-head">
            <span className={`pj-share-state pj-share-state--${share.state}`} data-testid="share-state">
              <Globe size={12} aria-hidden /> {t(`projects.run.share.state.${share.state}`)}
            </span>
            {share.expiresAt && (
              <span className="pj-share-meta" data-testid="share-expiry">
                {t('projects.run.share.expiresIn', { time: formatCountdown(share.expiresAt, now) })}
              </span>
            )}
          </div>
          {url && (
            <div className="pj-share-url-row">
              <code className="pj-share-url" data-testid="share-url">
                {url}
              </code>
              <button type="button" className="gd-btn" onClick={copy} data-testid="share-copy">
                {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? t('projects.run.share.copied') : t('projects.run.share.copy')}
              </button>
              <button type="button" className="gd-btn" onClick={() => openLink(url).catch(() => {})} data-testid="share-open">
                <ExternalLink size={13} /> {t('projects.run.share.open')}
              </button>
            </div>
          )}
          <BandwidthMeter share={share} />
          <p className="pj-share-meta" data-testid="share-hmr-note">
            {t('projects.run.share.hmrNote')}
          </p>
          <div>
            <button type="button" className="gd-btn pj-share-stop" onClick={() => stopShare(runKey)} data-testid="share-stop">
              <Square size={12} /> {t('projects.run.share.stop')}
            </button>
          </div>
        </div>
      </div>
      <RequestLog log={share.log || []} />
    </div>
  );
}
